import React, { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { MessageCircle, Image as ImageIcon, FileText, Video, Zap, Layers, ListFilter, Menu } from 'lucide-react';
import { useTheme } from '../ThemeContext';
import { getDynamicListItems } from '../dynamicVariables';

export const MessageNode = memo(({ data, selected }) => {
  const { isDark } = useTheme();
  const headerType = data.headerType || 'NONE';
  const buttons = data.buttons || [];

  return (
    <div className={`w-80 rounded-xl border transition-all ${
      isDark ? 'bg-[#1a1b1e] border-slate-800 shadow-xl' : 'bg-white border-slate-200/90 shadow-xs'
    } ${
      selected ? 'border-[#dc2742] ring-2 ring-[#dc2742]/30 shadow-md' : (isDark ? 'hover:border-slate-700' : 'hover:border-slate-300')
    }`}>
      {/* Target Input Handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="input"
        className={`!h-3.5 !w-3.5 !border-2 !bg-[#25d366] hover:!scale-125 transition-transform ${
          isDark ? '!border-slate-900' : '!border-white'
        }`}
      />

      {/* Node Header */}
      <div className={`flex items-center justify-between rounded-t-xl px-3.5 py-2.5 border-b ${
        isDark
          ? 'bg-gradient-to-r from-emerald-950/60 to-emerald-900/30 border-emerald-500/20'
          : 'bg-gradient-to-r from-emerald-50 to-teal-50/60 border-emerald-100'
      }`}>
        <div className="flex items-center gap-2">
          <div className={`flex h-6 w-6 items-center justify-center rounded-lg ${
            isDark ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-700'
          }`}>
            <MessageCircle className="h-3.5 w-3.5" />
          </div>
          <span className={`text-xs font-bold tracking-wide ${
            isDark ? 'text-emerald-200' : 'text-emerald-950'
          }`}>
            {data.label || 'WhatsApp Message'}
          </span>
        </div>
        {headerType !== 'NONE' && (
          <span className={`flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium ${
            isDark ? 'bg-emerald-500/10 text-emerald-300' : 'bg-emerald-100 text-emerald-700'
          }`}>
            {headerType === 'IMAGE' && <ImageIcon className="h-2.5 w-2.5" />}
            {headerType === 'DOCUMENT' && <FileText className="h-2.5 w-2.5" />}
            {headerType === 'VIDEO' && <Video className="h-2.5 w-2.5" />}
            {headerType}
          </span>
        )}
      </div>

      {/* WhatsApp Bubble Preview */}
      <div className="p-3.5 space-y-2.5">
        {data.mediaUrl && (
          <div className={`overflow-hidden rounded-lg border p-1 ${
            isDark ? 'border-slate-700 bg-slate-800/80' : 'border-slate-200 bg-slate-50'
          }`}>
            {headerType === 'IMAGE' ? (
              <img src={data.mediaUrl} alt="Header" className="h-24 w-full object-cover rounded" />
            ) : (
              <div className={`flex items-center gap-2 p-2 text-xs ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                <FileText className="h-4 w-4 text-emerald-500" />
                <span className="truncate">{data.mediaUrl}</span>
              </div>
            )}
          </div>
        )}

        {data.messageType === 'template' && (
          <div className="space-y-1">
            <div className={`flex items-center justify-between px-2.5 py-1 rounded-md text-[10px] font-semibold border ${
              isDark ? 'bg-amber-500/10 border-amber-500/30 text-amber-300' : 'bg-amber-50 border-amber-300 text-amber-800'
            }`}>
              <span className="truncate flex items-center gap-1.5">
                <Zap className="h-3 w-3 text-amber-500 shrink-0" />
                <span>{data.template_name || 'Select Template'}</span>
              </span>
              {Object.keys(data.template_variables || {}).length > 0 && (
                <span className="shrink-0 px-1.5 py-0.2 rounded text-[9px] bg-emerald-500/20 text-emerald-500 dark:text-emerald-400 font-mono font-bold">
                  {Object.keys(data.template_variables).length} vars mapped
                </span>
              )}
            </div>
          </div>
        )}

        <div className={`rounded-lg p-2.5 text-xs leading-relaxed whitespace-pre-wrap font-sans border ${
          isDark
            ? 'bg-emerald-950/30 border-emerald-900/40 text-slate-200'
            : 'bg-emerald-50/70 border-emerald-200 text-slate-800'
        }`}>
          {data.messageType === 'template' && data.template_variables && Object.keys(data.template_variables).length > 0 ? (
            (() => {
              const text = data.body || '';
              const parts = text.split(/(\{\{\s*[a-zA-Z0-9_]+\s*\}\})/g);
              return parts.map((part, idx) => {
                const match = part.match(/^\{\{\s*([a-zA-Z0-9_]+)\s*\}\}$/);
                if (match) {
                  const ph = match[1];
                  const mapped = data.template_variables[ph];
                  if (mapped) {
                    return (
                      <span key={idx} className="font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-500/20 px-1 rounded">
                        {mapped}
                      </span>
                    );
                  }
                }
                return <span key={idx}>{part}</span>;
              });
            })()
          ) : (
            data.body || 'Type your message text here...'
          )}
        </div>

        {/* Horizontal Carousel Preview on Canvas */}
        {data.carousel_cards && data.carousel_cards.length > 0 && (
          <div className="space-y-1.5 pt-1">
            <div className="flex items-center justify-between text-[10px] font-bold text-amber-500">
              <span className="flex items-center gap-1.5">
                <Layers className="h-3.5 w-3.5 text-amber-500" />
                Carousel ({data.carousel_cards.length} Cards)
              </span>
              <span className="text-[9px] text-slate-400">Horizontal scroll →</span>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1.5 scrollbar-thin">
              {data.carousel_cards.map((c, i) => {
                const cImg = (data.carousel_media_variables || {})[i] !== undefined 
                  ? data.carousel_media_variables[i] 
                  : (c.header_url || c.media_url);
                return (
                  <div key={i} className={`min-w-[130px] max-w-[130px] rounded-lg border overflow-hidden shrink-0 shadow-xs ${
                    isDark ? 'bg-slate-800/90 border-slate-700' : 'bg-slate-50 border-slate-200'
                  }`}>
                    {cImg && (
                      <div className="h-16 w-full bg-black/20 overflow-hidden">
                        <img src={cImg} alt={`Card ${i+1}`} className="h-full w-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                      </div>
                    )}
                    <div className="p-1.5 space-y-1">
                      <div className="text-[10px] font-medium text-slate-700 dark:text-slate-200 line-clamp-2">{c.body || `Card ${i+1}`}</div>
                      {Array.isArray(c.buttons) && c.buttons.length > 0 && (
                        <div className="text-center text-[9px] font-semibold py-0.5 px-1 rounded bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 truncate">
                          {c.buttons[0]?.text || c.buttons[0]?.title || 'Option'}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* WhatsApp Interactive List (Menu with up to 10 rows) */}
        {data.interactiveType === 'list' ? (
          (() => {
            const listSource = data.listSource || 'packages';
            const isCustom = listSource === 'custom';
            const listItems = isCustom ? (data.listCustomItems || []) : [];
            const isCustomBranching = isCustom && data.listBranching;
            const ctaText = data.listCtaText || (listSource === 'packages' ? 'View Packages' : listSource === 'classes' ? 'View Classes' : listSource === 'products' ? 'View Products' : 'Select Option');
            const targetVar = data.listVariable || (listSource === 'packages' ? 'selected_package' : listSource === 'classes' ? 'selected_class' : listSource === 'products' ? 'selected_product' : 'selected_item');

            return (
              <div className="space-y-2 pt-1 border-t border-slate-700/20">
                {/* CTA Button Badge */}
                <div className={`flex items-center justify-between rounded-lg border px-3 py-2 text-xs font-bold shadow-xs ${
                  isDark ? 'border-teal-500/40 bg-teal-950/40 text-teal-300' : 'border-teal-400 bg-teal-50 text-teal-800'
                }`}>
                  <span className="flex items-center gap-1.5 truncate">
                    <Menu className="h-3.5 w-3.5 text-teal-500 shrink-0" />
                    <span>{ctaText}</span>
                  </span>
                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-teal-500/20 text-teal-400">
                    {isCustom ? `${listItems.length} items` : `Live DB: ${listSource}`}
                  </span>
                </div>

                {/* Source Description */}
                <div className={`px-2 py-1 rounded text-[10px] flex items-center justify-between font-mono ${
                  isDark ? 'bg-slate-800/80 text-slate-400' : 'bg-slate-100 text-slate-600'
                }`}>
                  <span className="capitalize">
                    Source: <span className="font-bold text-teal-500">{listSource}</span>
                  </span>
                  <span className="text-[9px] text-emerald-500 font-bold">
                    → &#123;&#123;{targetVar}&#125;&#125;
                  </span>
                </div>

                {/* Custom Branching Handles per Row */}
                {isCustomBranching ? (
                  <div className="space-y-1.5 pt-1">
                    {listItems.map((item, idx) => (
                      <div
                        key={item.id || idx}
                        className={`relative flex items-center justify-between rounded-lg border px-2.5 py-1.5 text-xs font-semibold ${
                          isDark ? 'border-slate-700 bg-slate-800/80 text-slate-200' : 'border-slate-200 bg-slate-50 text-slate-800'
                        }`}
                      >
                        <span className="truncate max-w-[200px]">{item.title}</span>
                        <Handle
                          type="source"
                          position={Position.Right}
                          id={`list_${item.id || idx}`}
                          className={`!h-3 !w-3 !border-2 !bg-teal-500 hover:!scale-125 transition-transform ${
                            isDark ? '!border-slate-900' : '!border-white'
                          }`}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  /* Single Output Handle for Selected Item */
                  <div className={`flex items-center justify-between border-t pt-2 text-[10px] ${
                    isDark ? 'border-slate-800 text-slate-400' : 'border-slate-100 text-slate-500'
                  }`}>
                    <span>After Selection (Next Step):</span>
                    <Handle
                      type="source"
                      position={Position.Right}
                      id="output"
                      className={`!h-3.5 !w-3.5 !border-2 !bg-teal-500 hover:!scale-125 transition-transform ${
                        isDark ? '!border-slate-900' : '!border-white'
                      }`}
                    />
                  </div>
                )}
              </div>
            );
          })()
        ) : (
          <>
            {/* Buttons List */}
            {buttons.length > 0 && (
              <div className="space-y-1.5 pt-1">
                {buttons.map((btn, idx) => (
                  <div
                    key={btn.id || idx}
                    className={`relative flex items-center justify-between rounded-lg border px-3 py-1.5 text-xs font-semibold shadow-xs ${
                      isDark
                        ? 'border-slate-700 bg-slate-800/80 text-slate-200'
                        : 'border-slate-200 bg-slate-50 text-slate-800'
                    }`}
                  >
                    <span className="truncate">{btn.title}</span>
                    <Handle
                      type="source"
                      position={Position.Right}
                      id={`btn_${btn.id}`}
                      className={`!h-3 !w-3 !border-2 !bg-emerald-500 hover:!scale-125 transition-transform ${
                        isDark ? '!border-slate-900' : '!border-white'
                      }`}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Default Output Fallback Handle if no buttons */}
            {buttons.length === 0 && (
              <div className={`flex items-center justify-between border-t pt-2 text-[10px] ${
                isDark ? 'border-slate-800 text-slate-400' : 'border-slate-100 text-slate-500'
              }`}>
                <span>Next Step:</span>
                <Handle
                  type="source"
                  position={Position.Right}
                  id="output"
                  className={`!h-3.5 !w-3.5 !border-2 !bg-emerald-500 hover:!scale-125 transition-transform ${
                    isDark ? '!border-slate-900' : '!border-white'
                  }`}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
});
